import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { message } from 'antd'
import { useCompany } from '../CompanyContext'
import { checkIsLogged } from 'utils/UserAuthorization'
import { cleanUserToken } from 'utils/UserAuthorization'
import { getUserCachedData } from 'utils/UserAuthorization'
import { cacheClear } from 'utils/memoryCache'
import useSyncUserRole from 'hooks/useSyncUserRole'
import { resetSessionExpirationHandling } from 'utils/apiClient'

export const LoginContext = createContext()
export const LoginContextProvider = ({ children }) => {
  const userCachedData = getUserCachedData()
  const [isLogged, setIsLogged] = useState(checkIsLogged())
  const { updateCompanyFromUser, clearCompany } = useCompany?.() || {}
  
  // Ativa a sincronização de role quando o usuário está logado
  useSyncUserRole(isLogged)

  const performLogout = useCallback(() => {
    // Desconectar socket diretamente via window/global
    try {
      // Disparar evento customizado para o NotificationProvider desconectar
      window.dispatchEvent(new CustomEvent('logout'))
    } catch (err) {
      console.warn('Erro ao disparar evento de logout:', err)
    }
    
    localStorage.removeItem('user')
    // Importantíssimo para multi-tenant: limpar cache em memória ao trocar sessão
    // (evita carregar dashboard/config de uma empresa em outra)
    try { cacheClear() } catch (e) {}
    cleanUserToken()
    try { clearCompany && clearCompany() } catch (e) {}
    setIsLogged(false)
  }, [clearCompany])

  useEffect(() => {
    const handleSessionExpired = () => {
      performLogout()
      setTimeout(() => {
        message.destroy()
        message.warning('Sua sessão expirou. Faça login novamente.')
      }, 0)
    }

    window.addEventListener('session-expired', handleSessionExpired)
    return () => window.removeEventListener('session-expired', handleSessionExpired)
  }, [performLogout])

  const performLogin = data => {
    const user = JSON.stringify(data)
    localStorage.setItem('user', user)
    // Importantíssimo para multi-tenant: limpar cache em memória ao trocar sessão
    // (evita reutilizar respostas cacheadas de outro usuário/empresa)
    try { cacheClear() } catch (e) {}
    resetSessionExpirationHandling()
    setIsLogged(true)
    try { updateCompanyFromUser && updateCompanyFromUser(data) } catch(e) {}
  }

  return (
    <LoginContext.Provider
      value={{
        isLogged,
        performLogout,
        performLogin,
        userCachedData,
      }}
    >
      {children}
    </LoginContext.Provider>
  )
}

const useLogin = () => {
  return useContext(LoginContext)
}

export default useLogin
